import { useRef, useEffect, useState, useMemo } from "react";
import { useMapLibre } from "@/hooks/useMapLibre";
import { DeckOverlay } from "./DeckOverlay";
import { CollaborativeLayer } from "./CollaborativeLayer";
import { DrawingTools } from "./DrawingTools";
import { SelectionTools } from "./SelectionTools";
import { UserCursors } from "./UserCursors";
import { useMapStore } from "@/stores/mapStore";
import { Search, MousePointer2, Wind, Layers } from "lucide-react";

function CinematicIntro() {
  const [phase, setPhase] = useState<'visible' | 'fading' | 'gone'>('visible');

  useEffect(() => {
    const fadeTimer = setTimeout(() => setPhase('fading'), 2800);
    const goneTimer = setTimeout(() => setPhase('gone'), 4600);
    return () => { clearTimeout(fadeTimer); clearTimeout(goneTimer); };
  }, []);

  if (phase === 'gone') return null;

  return (
    <div
      className="absolute inset-0 z-40 pointer-events-none flex items-center justify-center"
      style={{
        opacity: phase === 'fading' ? 0 : 1,
        transition: 'opacity 1.8s ease-out',
      }}
    >
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
            <div className="w-4 h-4 rounded-full border-2 border-white" />
          </div>
        </div>
        <h1
          className="text-4xl font-bold text-white tracking-tight"
          style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8), 0 0 60px rgba(0,0,0,0.5)' }}
        >
          Overwatch
        </h1>
        <p
          className="text-sm text-white/60 mt-2 font-medium tracking-widest uppercase"
          style={{ textShadow: '0 1px 10px rgba(0,0,0,0.8)' }}
        >
          Common Operational Picture
        </p>
      </div>
    </div>
  );
}

const TIPS = [
  {
    icon: Search,
    keys: ['⌘', 'K'],
    title: 'Command Palette',
    desc: 'Search locations, toggle layers, switch themes',
  },
  {
    icon: MousePointer2,
    keys: ['1–7'],
    title: 'Drawing Tools',
    desc: 'Number keys to switch tools (1=Select, 2=Point, ...)',
  },
  {
    icon: Wind,
    keys: null,
    title: 'Live Wind',
    desc: 'Animated wind particles from real weather data',
  },
  {
    icon: Layers,
    keys: null,
    title: 'Collaborative',
    desc: 'Draw and annotate — changes sync in real time',
  },
];

function WelcomeGuide({ onDismiss }: { onDismiss: () => void }) {
  const [visible, setVisible] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    // Appear after the cinematic intro settles
    const timer = setTimeout(() => setVisible(true), 6500);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    if (dontShowAgain) {
      localStorage.setItem('overwatch:hideWelcome', '1');
    }
    setVisible(false);
    setTimeout(onDismiss, 400);
  };

  return (
    <div
      className="absolute bottom-6 left-1/2 z-40"
      style={{
        transform: `translateX(-50%) translateY(${visible ? '0' : '20px'})`,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.4s ease-out, transform 0.4s ease-out',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <div className="rounded-xl border border-border bg-card/95 backdrop-blur-md shadow-2xl px-5 py-4 w-[420px] max-w-[calc(100vw-2rem)]">
        <p className="text-xs font-medium text-foreground mb-3">Quick Start</p>

        <div className="space-y-2.5">
          {TIPS.map((tip) => (
            <div key={tip.title} className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <tip.icon size={14} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-foreground">{tip.title}</span>
                  {tip.keys && (
                    <div className="flex items-center gap-0.5">
                      {tip.keys.map((k) => (
                        <kbd
                          key={k}
                          className="inline-flex h-4 min-w-[16px] items-center justify-center rounded border border-border bg-muted px-1 text-[10px] text-muted-foreground"
                        >
                          {k}
                        </kbd>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">{tip.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-border accent-primary cursor-pointer"
            />
            <span className="text-[11px] text-muted-foreground">Don't show again</span>
          </label>
          <button
            onClick={handleDismiss}
            className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
          >
            Got it
          </button>
        </div>

        <div className="mt-3 pt-2 border-t border-border/50 flex items-center justify-center gap-3">
          <a
            href="https://github.com/danielyj147/overwatch"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-muted-foreground/60 hover:text-primary transition-colors"
          >
            GitHub
          </a>
          <span className="text-muted-foreground/30">·</span>
          <a
            href="https://www.linkedin.com/in/danielyj147/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-muted-foreground/60 hover:text-primary transition-colors"
          >
            Built by Daniel Jeong
          </a>
        </div>
      </div>
    </div>
  );
}

export function MapContainer() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const { isMapReady } = useMapStore();
  const [showWelcome, setShowWelcome] = useState(
    () => localStorage.getItem('overwatch:hideWelcome') !== '1'
  );

  // Set container after mount
  useEffect(() => {
    if (containerRef.current) {
      setContainer(containerRef.current);
    }
  }, []);

  // FIX: Memoize options to prevent infinite render loop.
  const mapOptions = useMemo(
    () => ({
      container,
      initialCenter: [-98.5, 39.5] as [number, number],
      initialZoom: 4,
    }),
    [container],
  );

  // Initialize MapLibre
  const { map } = useMapLibre(mapOptions);

  return (
    <div ref={containerRef} className="map-container relative w-full h-full">
      {/* Cinematic intro overlay */}
      {isMapReady && <CinematicIntro />}

      {/* Welcome guide — appears after cinematic intro */}
      {isMapReady && showWelcome && (
        <WelcomeGuide onDismiss={() => setShowWelcome(false)} />
      )}

      {/* Deck.gl overlay for dynamic layers */}
      {isMapReady && map && <DeckOverlay map={map} />}

      {/* Collaborative annotations layer */}
      {isMapReady && <CollaborativeLayer />}

      {/* Drawing interaction layer */}
      {isMapReady && map && <DrawingTools map={map} />}

      {/* Selection tools for multi-select */}
      {isMapReady && map && <SelectionTools map={map} />}

      {/* Remote user cursors */}
      {isMapReady && <UserCursors />}

      {/* Loading state */}
      {!isMapReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-background z-50">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <div className="w-4 h-4 rounded-full border-2 border-primary animate-pulse" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
